import { useReducer, useEffect, useRef } from 'react'
import {
    FormSection,
    UniversalDataTable,
    UniversalGoogleMap,
    UniversalReactSorter,
    Title,
    Grid,
    UniversalImageDisplay as UniversalImageDisplayer,
    UniversalAccordion,
} from '../../../../UniversalComponents'
import {
    Button,
    Typography,
    Accordion,
    AccordionDetails,
    AccordionSummary,
    CircularProgress,
    TextField,
    LinearProgress,
    FormControlLabel,
    Switch,
} from '@mui/material'
import BoatInsightCards from './BoatConfigInsightsCards'
import BoatConfigInsightsHistogram from './BoatConfigInsightsHistogram'
import BoatConfigInsightsTelemetry from './BoatConfigInsightsTelemetry'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import BoatUsers from './BoatConfigInsightsBoatUsers'
import { useSocket } from '../../../../../context/SocketContext'
import { useToastMessage } from '../../../../../context/ToastPopUpContext'
import { useAuth } from '../../../../../context/AuthenticationContext'
import { getBoatTelemetry } from '../../../../../apiRoutes'
import {
    parseTelemetryData,
    permissionsCheck,
} from '../../../../../utilities/functionTools'
import { SingleBoatDashboard } from '../../../../Analytics/Dashboard/components/SingleBoatDashboard'
import { SingleBoatChartViewTabs } from '../../../../Analytics/Dashboard/components/TabView'
import { useGlobalState } from '../../../../../context/GlobalStateContext'

const BoatConfigurationInsights = ({ row, openEdit, interval, editBoat }) => {
    const reducer = (state, newState) => ({ ...state, ...newState })

    const defaultState = {
        clusterList: [],
        draggable: [],
        telemetry: [],
        formattedData: [],
        histogramTelemetry: [],
        gps: { lat: 28, lng: -81 },
        signals: [],
        isLoaded: true,
        loading: true,
        filteredSignals: [],
        boatModelImage: '',
        boatInsights: row?.boatModel?.signalProfile?.boatInsights,
        boatStatus: '',
        telemetryPreferences: row?.telemetryPreferences || {
            ownerGps: true,
            geoFencing: false,
            guestGps: false,
            dealerGps: false,
            manufacturerGps: false,
            ownerSignalNotifications: true,
            guestSignalNotifications: false,
            dealerSignalNotifications: true,
            manufacturerSignalNotifications: false,
        },
    }

    const [state, setState] = useReducer(reducer, defaultState)
    const {
        telemetry,
        histogramTelemetry,
        signals,
        gps,
        filteredSignals,
        boatModelImage,
        isLoaded,
        loading,
        boatInsights,
        formattedData,
        boatStatus,
    } = state

    const {
        fullHin,
        boatModel,
        manufacturer,
        nickname,
        manufacturerName,
        owner,
        guests,
        telemetryPreferences,
        bluetoothEnabled,
        dealerVisibility,
    } = row

    const { apiSocket } = useSocket()
    const authentication = useAuth()
    const { auth } = authentication
    const { userPreferences, activeManufacturer } = auth
    const setToastMessage = useToastMessage()
    const visibleColumns = ['variable', 'latest_value', 'latest_timestamp']
    const defaultColumnVisibility = {
        variable: true,
        latest_value: true,
        latest_timestamp: true,
    }

    const style = {
        width: '100%',
        height: 500,
        backgroundColor: 'inherit',
    }

    const refreshCount = useRef(0)
    const { globalState } = useGlobalState()
    const { oupUser } = globalState

    const telemetryPreferenceLabel = (preference: string) => {
        let label: string = ''
        switch (preference) {
            case 'geoFencing':
                label = 'Enable Geofencing'
                break
            case 'ownerGps':
                label = 'Share Boat GPS'
                break
            case 'guestGps':
                label = 'Share Boat GPS with Guests'
                break
            case 'dealerGps':
                label = 'Share Boat GPS with Dealer'
                break
            case 'manufacturerGps':
                label = 'Share Boat GPS with Manufacturer'
                break
            case 'ownerSignalNotifications':
                label = 'Owner Recieves Signal Notifications'
                break
            case 'guestSignalNotifications':
                label = 'Guests Recieve Signal Notifications'
                break

            case 'dealerSignalNotifications':
                label = 'Dealer Recieves Signal Notifications'
                break
            case 'manufacturerSignalNotifications':
                label = 'Manufacturer Recieves Signal Notifications'
                break
            default:
                label = 'ADD ME TO telemetryPreferenceLabel.'
        }
        return label
    }

    // const getData = async (id: string) => {
    //     try {
    //         if (row?.device?._id) {
    //             const getHistogramTelemetryData = await getBoatTelemetry({
    //                 _id: row?.device?.deviceSerial,
    //             })
    //             if (
    //                 getHistogramTelemetryData &&
    //                 getHistogramTelemetryData.length !== 0
    //             ) {
    //                 setState({
    //                     histogramTelemetry: getHistogramTelemetryData,
    //                     loading: false,
    //                 })
    //             }
    //         } else {
    //             setToastMessage(
    //                 'There is no device paired with this boat.',
    //                 'info'
    //             )
    //         }
    //     } catch (error: any) {
    //         setToastMessage(error, 'error')
    //         console.error(error, 'error')
    //     }
    // }

    useEffect(() => {
        setState({ boatModelImage: boatModel?.modelImageUrl })
        if (apiSocket) {
            apiSocket?.off('liveDataRes')

            apiSocket?.emit('liveData', {
                fullHin,
                type: 'webSignals',
                userPreferences,
            })
            const handleLiveDataRes = (data) => {
                if (data) {
                    const boatStatus = data?.status?.lastest_value

                    setState({
                        telemetry: data,
                        signals: data.signals,
                        gps: {
                            lat: data?.GPS_Latitude?.latest_value,
                            lng: data?.GPS_Longitude?.latest_value,
                        },
                    })
                }
                refreshCount.current = refreshCount.current + 1
                return refreshCount
            }
            apiSocket?.on('liveDataRes', handleLiveDataRes)

            return () => {
                apiSocket?.off('liveDataRes', handleLiveDataRes)
            }
        }
    }, [apiSocket])

    // useEffect(() => {
    //     if (row?.id && histogramTelemetry.length === 0) {
    //         getData(row?.id)
    //     }
    // }, [])

    useEffect(() => {
        interval.current = setInterval(() => {
            apiSocket?.emit('liveData', {
                fullHin,
                type: 'webSignals',
                userPreferences,
            })
        }, 10000)

        return () => {
            clearInterval(interval.current)
        }
    }, [interval])

    useEffect(() => {
        if (formattedData && formattedData?.length !== 0) {
            setTimeout(() => setState({ isLoaded: false }), 1000)
        }
    }, [formattedData])

    useEffect(() => {
        if (
            telemetry &&
            typeof telemetry === 'object' &&
            Object.keys(telemetry).length > 0
        ) {
            const formatData = Object.keys(telemetry)
                ?.map((key, index) => ({
                    id: `${key}-${Date.now()}-${index}`,
                    variable: key,
                    latest_value: telemetry[key]?.latest_value || '',
                    latest_timestamp: telemetry[key]?.latest_timestamp || '',
                }))
                .filter(({ variable }) => variable !== 'signals')
                .filter(({ id, variable }) => id && variable) // Ensure both id and variable exist

            setState({ formattedData: formatData })
        } else {
            setState({ formattedData: [] })
        }
    }, [telemetry])

    useEffect(() => {
        setState({ filteredSignals: signals })
    }, [signals])

    const manufacturerCheck = activeManufacturer === manufacturerName

    const isDealer = oupUser?.userProfile?.dealerProfile
    const dealerVisibilityPermissions = isDealer ? dealerVisibility : false

    if (isLoaded) {
        return (
            <Grid item xs={12} md={12} lg={12}>
                <Typography
                    align="center"
                    sx={{ marginTop: 50, marginBottom: 50 }}
                >
                    <LinearProgress />
                </Typography>
            </Grid>
        )
    }

    return (
        <Grid container spacing={2} sx={{ marginTop: 8 }}>
            {formattedData &&
            formattedData?.length !== 1 &&
            (isDealer ? dealerVisibilityPermissions : true) ? (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} style={{ marginTop: -40 }}>
                                {manufacturerCheck && editBoat && !isDealer && (
                                    <FormSection>
                                        <Grid item xs={12}>
                                            <Button
                                                onClick={openEdit}
                                                variant="contained"
                                                color="primary"
                                                fullWidth
                                                sx={{
                                                    height: 55,
                                                }}
                                            >
                                                Edit Boat Info
                                            </Button>
                                        </Grid>
                                    </FormSection>
                                )}
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FormSection
                                    title={`Boat Status: ${
                                        boatStatus ? 'Online' : 'Offline'
                                    }`}
                                ></FormSection>
                                <Grid>
                                    <Grid item xs={12}>
                                        <UniversalImageDisplayer
                                            url={
                                                boatModelImage ||
                                                row?.modelImageUrl
                                            }
                                            alt={`${boatModel?.modelYear} - ${boatModel?.modelYear}`}
                                            title={
                                                manufacturer?.manufacturerName ||
                                                row?.manufacturer
                                            }
                                            subtitle={`${
                                                boatModel?.modelYear ||
                                                row?.modelYear
                                            } - ${
                                                boatModel?.model || row?.model
                                            }`}
                                            width={'55%'}
                                            height={'55%'}
                                        />
                                    </Grid>
                                </Grid>

                                <FormSection title="Gps Data">
                                    <Grid item xs={12}>
                                        <UniversalGoogleMap
                                            gps={gps}
                                            style={style}
                                        />
                                    </Grid>
                                </FormSection>
                            </Grid>

                            <Grid item xs={12} md={8}>
                                <Grid item xs={12}>
                                    <FormSection title={`${fullHin}'s Details`}>
                                        <Grid
                                            item
                                            xs={6}
                                            sx={{ marginBottom: 5 }}
                                        >
                                            <TextField
                                                label={'Signal Profile'}
                                                value={
                                                    boatModel?.signalProfile
                                                        ?.profileName ||
                                                    row.profileName
                                                }
                                                disabled={true}
                                                fullWidth={true}
                                            />
                                        </Grid>
                                        <Grid
                                            item
                                            xs={6}
                                            sx={{ marginBottom: 5 }}
                                        >
                                            <TextField
                                                label={'Device Serial'}
                                                value={row?.deviceSerial}
                                                disabled={true}
                                                fullWidth={true}
                                            />
                                        </Grid>

                                        <Grid
                                            item
                                            xs={6}
                                            sx={{ marginBottom: 5 }}
                                        >
                                            <TextField
                                                label={'Nickname'}
                                                value={nickname}
                                                disabled={true}
                                                fullWidth={true}
                                            />
                                        </Grid>
                                        {owner && (
                                            <Grid
                                                item
                                                xs={6}
                                                sx={{ marginBottom: 5 }}
                                            >
                                                <TextField
                                                    label="Owner"
                                                    value={owner.userEmail}
                                                    disabled={true}
                                                    fullWidth={true}
                                                />
                                            </Grid>
                                        )}
                                        {guests && guests.length !== 0 && (
                                            <Grid item xs={12} md={9}>
                                                <TextField
                                                    label="Guests"
                                                    value={guests?.map(
                                                        (guest: any, index) =>
                                                            index + 1 !==
                                                            guests.length
                                                                ? `${guest?.userEmail}`
                                                                : ` ${guest?.userEmail}`
                                                    )}
                                                    disabled={true}
                                                    fullWidth={true}
                                                />
                                            </Grid>
                                        )}
                                        <Grid
                                            item
                                            xs={6}
                                            sx={{ marginBottom: 5 }}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        disabled={true}
                                                        checked={
                                                            row?.bluetoothEnabled
                                                        }
                                                        inputProps={{
                                                            'aria-label':
                                                                'controlled',
                                                        }}
                                                        id="switch.bluetoothEnabled"
                                                        name="switch.bluetoothEnabled"
                                                        key="switch.bluetoothEnabled"
                                                    />
                                                }
                                                label={
                                                    bluetoothEnabled
                                                        ? 'Bluetooth Enabled'
                                                        : 'Bluetooth Disabled'
                                                }
                                                key="switch.bluetoothEnabled"
                                            />
                                        </Grid>

                                        <Grid
                                            item
                                            xs={12}
                                            sx={{ marginBottom: 5 }}
                                        >
                                            <UniversalAccordion
                                                accordionTitle="Telemetry Preferences"
                                                accordionTitleDescription="Enabled Telemetry Preferences"
                                            >
                                                {telemetryPreferences &&
                                                    Object.keys(
                                                        telemetryPreferences
                                                    )?.length !== 0 &&
                                                    Object.keys(
                                                        telemetryPreferences
                                                    ).map((preference) => (
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    inputProps={{
                                                                        'aria-label':
                                                                            'controlled',
                                                                    }}
                                                                    id={`switch.${preference}`}
                                                                    name={`switch.${preference}`}
                                                                    checked={
                                                                        telemetryPreferences[
                                                                            preference
                                                                        ]
                                                                    }
                                                                    key={`switch.${preference}`}
                                                                />
                                                            }
                                                            label={telemetryPreferenceLabel(
                                                                preference
                                                            )}
                                                            sx={
                                                                telemetryPreferences[
                                                                    preference
                                                                ]
                                                                    ? {
                                                                          color: 'primary',
                                                                      }
                                                                    : {
                                                                          color: 'grey',
                                                                      }
                                                            }
                                                            key={`switch.${preference}`}
                                                        />
                                                    ))}
                                            </UniversalAccordion>
                                        </Grid>
                                    </FormSection>
                                </Grid>
                                <FormSection title={'Assigned Boat Users'}>
                                    <Grid item xs={12}>
                                        <BoatUsers
                                            boatId={row?._id || row.id}
                                            isDealer={isDealer}
                                        />
                                    </Grid>
                                </FormSection>
                                <FormSection title={'Live Signal Data'}>
                                    <Grid item xs={12}>
                                        <UniversalReactSorter
                                            list={filteredSignals}
                                            setList={(drag) =>
                                                setState({
                                                    filteredSignals: drag,
                                                })
                                            }
                                            group="fixed"
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'row',
                                                justifyContent: 'center',
                                                flexWrap: 'wrap',
                                                marginTop: 5,
                                                width: '100%',
                                            }}
                                            disabled={true}
                                        >
                                            {filteredSignals?.length !== 0 &&
                                                filteredSignals?.map(
                                                    ({
                                                        id,
                                                        image,
                                                        description,
                                                        title,
                                                        unitOfMeasurement,
                                                        convertedValue,
                                                        convertedUOM,
                                                        timestamp,
                                                    }) => (
                                                        <BoatInsightCards
                                                            image={image}
                                                            key={`${id}.card`}
                                                            description={
                                                                convertedValue
                                                                    ? convertedValue
                                                                    : description
                                                            }
                                                            timestamp={
                                                                timestamp
                                                            }
                                                            unitOfMeasurement={
                                                                convertedUOM
                                                                    ? convertedUOM
                                                                    : unitOfMeasurement
                                                            }
                                                            title={title}
                                                            disabled={true}
                                                        />
                                                    )
                                                )}
                                        </UniversalReactSorter>
                                    </Grid>
                                </FormSection>
                                {boatInsights && boatInsights.length !== 0 && (
                                    <BoatConfigInsightsTelemetry
                                        boatInsights={boatInsights}
                                        telemetry={telemetry}
                                    />
                                )}
                            </Grid>

                            <Grid item xs={12}>
                                {/* <SingleBoatDashboard
                                    socketData={telemetry}
                                    row={row}
                                /> */}
                                <SingleBoatChartViewTabs
                                    row={row}
                                    data={telemetry}
                                />
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <Accordion elevation={4}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                >
                                    <Typography
                                        variant="h5"
                                        color={'primary'}
                                        align="center"
                                    >
                                        Live Data Table
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid item xs={12}>
                                        {formattedData &&
                                        formattedData?.length !== 0 ? (
                                            <UniversalDataTable
                                                data={formattedData.filter(
                                                    (item) =>
                                                        item.id && item.variable
                                                )}
                                                visibleColumns={visibleColumns}
                                                getRowData={() => {}}
                                                getColumnHeaderVisibility={() => {}}
                                                columnsAvailable={
                                                    defaultColumnVisibility
                                                }
                                                checkboxSelection={false}
                                                columnWidths={{
                                                    variable: 200,
                                                    latest_value: 300,
                                                    latest_timestamp: 200,
                                                }}
                                            />
                                        ) : (
                                            <h1>
                                                No Data available.
                                                {row?.fullHin}
                                            </h1>
                                        )}
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>
                        </Grid>
                    </Grid>
                </Grid>
            ) : (
                <Grid item xs={12} sx={{ margin: 5, textAlign: 'center' }}>
                    <Grid container spacing={2}>
                        <Grid item md={4} xs={12}>
                            <FormSection title="Boat Model">
                                <Grid item xs={12}>
                                    <UniversalImageDisplayer
                                        url={
                                            boatModelImage || row?.modelImageUrl
                                        }
                                        alt={`${boatModel?.modelYear} - ${boatModel?.modelYear}`}
                                        title={
                                            manufacturer?.manufacturerName ||
                                            row?.manufacturer
                                        }
                                        subtitle={`${
                                            boatModel?.modelYear ||
                                            row?.modelYear
                                        }  ${boatModel?.model || row?.model}`}
                                    />
                                </Grid>
                            </FormSection>
                        </Grid>
                        <Grid item md={8} xs={12} sx={{ textAlign: 'center' }}>
                            <FormSection
                                title="Edit Boat Details"
                                titleBottom={'No Telemetry Available'}
                            >
                                {editBoat && !isDealer && (
                                    <Grid item xs={12} md={12}>
                                        <Button
                                            onClick={openEdit}
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                            style={{
                                                height: 55,
                                            }}
                                        >
                                            Edit Boat Info
                                        </Button>
                                    </Grid>
                                )}
                            </FormSection>
                            <Grid item xs={12} md={12}>
                                <FormSection title={`${fullHin}'s Details`}>
                                    <Grid
                                        item
                                        md={6}
                                        xs={12}
                                        sx={{ marginBottom: 5 }}
                                    >
                                        <TextField
                                            label={'Signal Profile'}
                                            value={
                                                boatModel?.signalProfile
                                                    ?.profileName
                                            }
                                            disabled={true}
                                            fullWidth={true}
                                        />
                                    </Grid>
                                    <Grid
                                        item
                                        xs={12}
                                        md={4}
                                        sx={{ marginBottom: 5 }}
                                    >
                                        <TextField
                                            label={'Nickname'}
                                            value={nickname || 'None'}
                                            disabled={true}
                                            fullWidth={true}
                                        />
                                    </Grid>
                                </FormSection>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            )}
        </Grid>
    )
}

export default BoatConfigurationInsights
